import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { supabase, type Resource } from '@/lib/supabase'
import { Button, Card, ErrorNote } from '@/components/ui'

// Mirrors allowed_mime_types on the private `resources` bucket
// (migration 005). Checked here so a wrong file type fails before the upload
// starts rather than after; the bucket still enforces it.
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
  zip: 'application/zip',
}

// The bucket's own limit. The Supabase plan can impose a lower global cap
// (50 MB on Free); that surfaces as the storage error message on upload.
const MAX_BYTES = 256 * 1024 * 1024

const ACCEPT = Object.keys(MIME_BY_EXTENSION).map((ext) => `.${ext}`).join(',')

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

// Storage keys reject most non-ASCII, so "Kōrero Pūtea.pdf" becomes
// "Korero-Putea.pdf". Macrons are folded rather than dropped.
function safeFileName(name: string): string {
  const ext = extensionOf(name)
  const base = ext ? name.slice(0, -(ext.length + 1)) : name
  const cleaned = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return `${cleaned || 'file'}${ext ? `.${ext}` : ''}`
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileNameOf(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1)
}

/**
 * Work plan 3.4. Attaches a file to a resource in the private `resources`
 * bucket and records its key on the row. Only KTM can write here (storage
 * policy "resources bucket: ktm manages"); partners never touch the bucket
 * and reach files only through the resource-signed-url function.
 *
 * Key layout: <resource id>/<version>/<file name>. Replacing a file removes
 * the previous object once the row points at the new one, so the row is
 * always the single source of truth for what partners download.
 */
export function ResourceFileSection({
  row,
  onChange,
}: {
  row: Resource
  onChange: (row: Resource) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const ext = extensionOf(file.name)
      const contentType = MIME_BY_EXTENSION[ext]
      if (!contentType) {
        throw new Error(
          `That file type isn't allowed. Use one of: ${Object.keys(MIME_BY_EXTENSION).join(', ')}.`,
        )
      }
      if (file.size > MAX_BYTES) {
        throw new Error(`That file is ${formatBytes(file.size)}; the limit is 256 MB.`)
      }

      const folder = row.version.replace(/[^A-Za-z0-9._-]+/g, '-')
      const path = `${row.id}/${folder}/${safeFileName(file.name)}`

      const { error: uploadError } = await supabase.storage
        .from('resources')
        .upload(path, file, { contentType, upsert: true })
      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`)

      const { data, error: updateError } = await supabase
        .from('resources')
        .update({ storage_path: path, file_size_bytes: file.size })
        .eq('id', row.id)
        .select()
        .single()
      if (updateError) {
        // The row still points at the old file, so the new object is an orphan.
        await supabase.storage.from('resources').remove([path])
        throw updateError
      }

      if (row.storage_path && row.storage_path !== path) {
        // Best effort: a leftover object is harmless, it is never signed.
        await supabase.storage.from('resources').remove([row.storage_path])
      }
      return data as Resource
    },
    onSuccess: (updated) => onChange(updated),
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: async () => {
      if (!row.storage_path) return row
      if (!window.confirm(`Remove "${fileNameOf(row.storage_path)}" from this resource?`)) return row
      const { data, error: updateError } = await supabase
        .from('resources')
        .update({ storage_path: null, file_size_bytes: null })
        .eq('id', row.id)
        .select()
        .single()
      if (updateError) throw updateError
      await supabase.storage.from('resources').remove([row.storage_path])
      return data as Resource
    },
    onSuccess: (updated) => onChange(updated),
    onError: (e: Error) => setError(e.message),
  })

  // KTM reads the bucket directly (its storage policy covers select), so this
  // preview does not go through resource-signed-url and is not access-logged.
  const preview = useMutation({
    mutationFn: async () => {
      if (!row.storage_path) return
      const { data, error: signError } = await supabase.storage
        .from('resources')
        .createSignedUrl(row.storage_path, 60, { download: true })
      if (signError || !data) throw new Error(signError?.message ?? 'Could not open the file.')
      window.location.assign(data.signedUrl)
    },
    onError: (e: Error) => setError(e.message),
  })

  const busy = upload.isPending || remove.isPending
  // Unattaching the only body of a published resource would break the
  // resources_published_needs_body constraint; say so instead of bouncing.
  const removeBlocked = row.is_published && !row.video_url

  if (row.kind === 'video') {
    return (
      <Card className="mb-4 max-w-3xl">
        <h2 className="text-base font-medium text-ink">File</h2>
        <p className="mt-1 text-sm text-inksoft">
          Video is streamed from its host and never uploaded here (R17). Change the kind to
          document or template to attach a file.
        </p>
      </Card>
    )
  }

  return (
    <Card className="mb-4 max-w-3xl">
      <h2 className="text-base font-medium text-ink">File</h2>

      {row.storage_path ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="font-medium text-ink">{fileNameOf(row.storage_path)}</span>
          <span className="text-inksoft">{formatBytes(row.file_size_bytes)}</span>
          <button
            type="button"
            onClick={() => {
              setError(null)
              preview.mutate()
            }}
            disabled={preview.isPending}
            className="text-pounamu underline underline-offset-4 hover:no-underline disabled:opacity-60"
          >
            {preview.isPending ? 'Opening…' : 'Download'}
          </button>
        </div>
      ) : (
        <p className="mt-1 text-sm text-inksoft">
          No file yet. Partners can't open this resource, and it can't be published, until one is
          attached.
        </p>
      )}

      <p className="mt-2 text-xs text-inksoft">
        PDF, Word, PowerPoint, Excel, PNG, JPEG, SVG or zip, up to 256 MB. Uploading replaces the
        current file for every partner straight away — for a new edition, change the version and
        save first.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (!file) return
          setError(null)
          upload.mutate(file)
        }}
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button type="button" onClick={() => inputRef.current?.click()} disabled={busy}>
          {upload.isPending ? 'Uploading…' : row.storage_path ? 'Replace file' : 'Upload file'}
        </Button>
        {row.storage_path && (
          <Button
            type="button"
            variant="quiet"
            onClick={() => {
              setError(null)
              remove.mutate()
            }}
            disabled={busy || removeBlocked}
            title={removeBlocked ? 'Unpublish the resource before removing its file.' : undefined}
          >
            {remove.isPending ? 'Removing…' : 'Remove file'}
          </Button>
        )}
      </div>
      {removeBlocked && row.storage_path && (
        <p className="mt-2 text-xs text-inksoft">Unpublish the resource to remove its file.</p>
      )}

      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
    </Card>
  )
}
